import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FabricObject, FabricImage, IText } from 'fabric';
import { useDesignStore } from '@/stores';
import { cn } from '@/lib/utils';
import { isSystemLayer } from '@/types/canvas';
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Trash2,
  GripVertical,
  Image as ImageIcon,
  Type,
  Square,
  X,
} from 'lucide-react';

interface LayerItem {
  id: string;
  object: FabricObject;
  name: string;
  type: 'image' | 'text' | 'shape';
  visible: boolean;
  locked: boolean;
  thumbnail: string | null;
}

interface LayersPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * LayersPanel - Flyout panel showing all canvas layers
 *
 * Features:
 * - Thumbnail preview for images
 * - Text preview for text objects
 * - Visibility toggle
 * - Lock toggle
 * - Click to select
 * - Drag to reorder
 * - Delete layer
 */
export function LayersPanel({ isOpen, onClose }: LayersPanelProps) {
  const { t } = useTranslation();
  const { canvas, selectedObjects, saveToHistory } = useDesignStore();
  const [layers, setLayers] = useState<LayerItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Update layers when canvas changes
  const updateLayers = useCallback(() => {
    if (!canvas) return;

    // Filter out system layers (background, mockup, print area border)
    const objects = canvas
      .getObjects()
      .filter((obj) => !isSystemLayer(obj.name));
    const layerItems: LayerItem[] = objects.map((obj, index) => {
      let name = t('creator.layers.layerName', { index: index + 1 });
      let type: LayerItem['type'] = 'shape';
      let thumbnail: string | null = null;

      // Determine type and name
      if (obj.type === 'image') {
        type = 'image';
        name =
          (obj as FabricImage).getSrc()?.split('/').pop() ||
          t('creator.layers.imageName');
        // Get thumbnail from image source
        thumbnail = (obj as FabricImage).getSrc() || null;
      } else if (
        obj.type === 'i-text' ||
        obj.type === 'text' ||
        obj.type === 'textbox'
      ) {
        type = 'text';
        const textObj = obj as IText;
        name = textObj.text?.substring(0, 20) || t('creator.layers.textName');
        if (textObj.text && textObj.text.length > 20) {
          name += '...';
        }
      }

      return {
        id: `layer-${index}`,
        object: obj,
        name,
        type,
        visible: obj.visible !== false,
        locked: obj.selectable === false,
        thumbnail,
      };
    });

    // Reverse to show top layers first
    setLayers(layerItems.reverse());
  }, [canvas, t]);

  // Listen to canvas events
  useEffect(() => {
    if (!canvas) return;

    updateLayers();

    const events = [
      'object:added',
      'object:removed',
      'object:modified',
    ] as const;
    events.forEach((event) => canvas.on(event, updateLayers));

    return () => {
      events.forEach((event) => canvas.off(event, updateLayers));
    };
  }, [canvas, updateLayers]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // Check if click is on the layers button
        const layersButton = document.querySelector('[data-layers-button]');
        if (layersButton && layersButton.contains(e.target as Node)) {
          return;
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Select layer on canvas
  const handleSelectLayer = (layer: LayerItem) => {
    if (!canvas || layer.locked) return;
    canvas.setActiveObject(layer.object);
    canvas.requestRenderAll();
  };

  // Toggle visibility
  const handleToggleVisibility = (layer: LayerItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canvas) return;

    layer.object.set('visible', !layer.visible);
    canvas.requestRenderAll();
    updateLayers();
    saveToHistory('visibility');
  };

  // Toggle lock
  const handleToggleLock = (layer: LayerItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canvas) return;

    const newLocked = !layer.locked;
    layer.object.set({
      selectable: !newLocked,
      evented: !newLocked,
    });

    if (newLocked && selectedObjects.includes(layer.object)) {
      canvas.discardActiveObject();
    }

    canvas.requestRenderAll();
    updateLayers();
    saveToHistory('lock');
  };

  // Delete layer
  const handleDeleteLayer = (layer: LayerItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canvas) return;

    canvas.remove(layer.object);
    canvas.requestRenderAll();
    saveToHistory('delete');
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    // Reorder layers
    const newLayers = [...layers];
    const draggedLayer = newLayers[draggedIndex];
    if (!draggedLayer) return;
    newLayers.splice(draggedIndex, 1);
    newLayers.splice(index, 0, draggedLayer);
    setLayers(newLayers);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    if (!canvas || draggedIndex === null) return;

    // Apply new order to canvas (reverse because we display reversed)
    const reversedLayers = [...layers].reverse();
    const objects = canvas.getObjects();

    // Clear all objects and re-add in new order
    reversedLayers.forEach((layer, index) => {
      const currentIndex = objects.indexOf(layer.object);
      if (currentIndex !== index && currentIndex !== -1) {
        // Use sendToBack/bringToFront pattern for reordering
        canvas.sendObjectToBack(layer.object);
      }
    });

    // Re-add in correct order
    reversedLayers.forEach((layer) => {
      canvas.bringObjectToFront(layer.object);
    });

    canvas.requestRenderAll();
    saveToHistory('reorder');
    setDraggedIndex(null);
  };

  // Get icon for layer type
  const getLayerIcon = (type: LayerItem['type']) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="w-4 h-4 text-mute" />;
      case 'text':
        return <Type className="w-4 h-4 text-mute" />;
      default:
        return <Square className="w-4 h-4 text-mute" />;
    }
  };

  // Check if layer is selected
  const isSelected = (layer: LayerItem) => {
    return selectedObjects.includes(layer.object);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className={cn(
        'absolute left-14 top-0 bottom-16 z-50',
        'w-64 bg-paper border-r border-line ring-1 ring-line',
        'flex flex-col',
        'animate-in slide-in-from-left-2 duration-200'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-line">
        <h3 className="font-semibold text-ink">{t('creator.layers.title')}</h3>
        <button
          onClick={onClose}
          className="p-1 text-mute hover:text-ink-2 hover:bg-paper-2 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Layers List */}
      <div className="flex-1 overflow-y-auto p-2">
        {layers.length === 0 ? (
          <div className="text-center text-mute py-8">
            <Square className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('creator.layers.empty')}</p>
            <p className="text-xs mt-1">{t('creator.layers.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {layers.map((layer, index) => (
              <div
                key={layer.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onClick={() => handleSelectLayer(layer)}
                className={cn(
                  'flex items-center gap-2 p-2 rounded-control cursor-pointer',
                  'border border-transparent',
                  'transition-all duration-150',
                  isSelected(layer)
                    ? 'bg-primary-50 border-primary-200'
                    : 'hover:bg-paper',
                  layer.locked && 'opacity-60',
                  draggedIndex === index && 'opacity-50 scale-95'
                )}
              >
                {/* Drag Handle */}
                <div className="cursor-grab active:cursor-grabbing text-ink-3 hover:text-mute">
                  <GripVertical className="w-4 h-4" />
                </div>

                {/* Thumbnail / Icon */}
                <div className="w-10 h-10 rounded bg-paper-2 flex items-center justify-center overflow-hidden shrink-0">
                  {layer.thumbnail ? (
                    <img
                      src={layer.thumbnail}
                      alt={layer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : layer.type === 'text' ? (
                    <span
                      className="text-xs font-medium text-ink-2 truncate px-1"
                      style={{ maxWidth: '36px' }}
                    >
                      {layer.name.substring(0, 2)}
                    </span>
                  ) : (
                    getLayerIcon(layer.type)
                  )}
                </div>

                {/* Layer Name */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink-2 truncate">
                    {layer.name}
                  </p>
                  <p className="text-xs text-mute capitalize">
                    {t(`creator.layers.type.${layer.type}`)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {/* Visibility Toggle */}
                  <button
                    onClick={(e) => handleToggleVisibility(layer, e)}
                    className={cn(
                      'p-1.5 rounded hover:bg-paper-2',
                      layer.visible ? 'text-mute' : 'text-ink-3'
                    )}
                    title={
                      layer.visible
                        ? t('creator.layers.hide')
                        : t('creator.layers.show')
                    }
                  >
                    {layer.visible ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </button>

                  {/* Lock Toggle */}
                  <button
                    onClick={(e) => handleToggleLock(layer, e)}
                    className={cn(
                      'p-1.5 rounded hover:bg-paper-2',
                      layer.locked ? 'text-warning' : 'text-mute'
                    )}
                    title={
                      layer.locked
                        ? t('creator.layers.unlock')
                        : t('creator.layers.lock')
                    }
                  >
                    {layer.locked ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      <Unlock className="w-4 h-4" />
                    )}
                  </button>

                  {/* Delete */}
                  <button
                    onClick={(e) => handleDeleteLayer(layer, e)}
                    className="p-1.5 rounded text-mute hover:text-danger hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)]"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-line text-xs text-mute">
        {t('creator.layers.footer', { count: layers.length })}
      </div>
    </div>
  );
}
